import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFallbackSnapshot, createFallbackTerms } from "../lib/fallbackWorld";
import {
  freezeWorldDisplay,
  logicalNAt,
  mergeTermWindows,
  parseTermsPayload,
  parseWorldBatch,
  parseWorldSnapshot,
  selectTermWindowAtOrBefore,
  serverOffsetAtRequestMidpoint,
  termAtOrBefore,
  termWindowNeedsRecovery,
  type FrozenWorldDisplay,
  type WorldTerm,
  type WorldView,
} from "../lib/world";

const fallbackTerms = createFallbackTerms();
const fallbackSnapshot = createFallbackSnapshot(fallbackTerms);
const MAX_CLIENT_TERMS = 12_000;
const INITIAL_TERM_LIMIT = 8_192;
const REFRESH_TERM_LIMIT = 512;
const DISPLAY_TERM_LIMIT = 8_192;

function websocketUrl(): string {
  const url = new URL("/api/ws", window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.href;
}

function extractMessageData(value: unknown): { type: string; data?: unknown } | null {
  if (typeof value !== "object" || value === null || !("type" in value)) return null;
  const candidate = value as { type?: unknown; data?: unknown };
  return typeof candidate.type === "string" ? { type: candidate.type, data: candidate.data } : null;
}

export interface WorldStreamResult {
  world: WorldView;
  liveTerm: WorldTerm;
  displayTerm: WorldTerm;
  displayTerms: WorldTerm[];
  liveTerms: WorldTerm[];
  recentTerms: WorldTerm[];
  liveN: bigint;
  behindBy: bigint;
  isPaused: boolean;
  pauseDisplay: () => void;
  returnToLive: () => void;
  refresh: () => Promise<void>;
}

export function useWorldStream(): WorldStreamResult {
  const [world, setWorld] = useState<WorldView>({
    snapshot: fallbackSnapshot,
    terms: fallbackTerms,
    connection: "connecting",
    lastSyncMs: null,
    usingFallback: true,
  });
  const [clock, setClock] = useState(fallbackSnapshot.serverTimeMs);
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [frozenDisplay, setFrozenDisplay] = useState<FrozenWorldDisplay | null>(null);
  const refreshRef = useRef<(() => Promise<void>) | null>(null);
  const worldRef = useRef(world);

  useEffect(() => {
    worldRef.current = world;
  }, [world]);

  useEffect(() => {
    let timer: number | null = null;
    let disposed = false;
    const tick = () => {
      if (disposed) return;
      const now = Date.now();
      setClock(now);
      const estimatedServerNow = now + serverOffsetMs;
      const worldAge = Math.max(0, estimatedServerNow - world.snapshot.startedAtMs);
      const remainder = worldAge % world.snapshot.tickMs;
      const delay = Math.max(16, world.snapshot.tickMs - remainder + 8);
      timer = window.setTimeout(tick, delay);
    };
    tick();
    return () => {
      disposed = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [serverOffsetMs, world.snapshot.startedAtMs, world.snapshot.tickMs]);

  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let reconnectAttempt = 0;
    let refreshPromise: Promise<void> | null = null;
    let hasInitialTerms = false;
    const controller = new AbortController();

    const refresh = async () => {
      if (refreshPromise) return refreshPromise;
      refreshPromise = (async () => {
        try {
          const snapshotRequestStartedAtMs = Date.now();
          const snapshotResponse = await fetch("/api/snapshot", {
            signal: controller.signal,
            headers: { Accept: "application/json" },
          });
          const snapshotResponseReceivedAtMs = Date.now();
          if (!snapshotResponse.ok) {
            throw new Error(`snapshot request failed (${snapshotResponse.status})`);
          }
          const snapshot = parseWorldSnapshot(await snapshotResponse.json());
          const termLimit = hasInitialTerms ? REFRESH_TERM_LIMIT : INITIAL_TERM_LIMIT;
          const termsResponse = await fetch(
            `/api/recent?through=${encodeURIComponent(snapshot.generatedThrough)}&limit=${termLimit}`,
            { signal: controller.signal, headers: { Accept: "application/json" } },
          );
          if (!termsResponse.ok) {
            throw new Error(`terms request failed (${termsResponse.status})`);
          }
          const lightweightTerms = parseTermsPayload(await termsResponse.json());
          const baselineTerms = worldRef.current.usingFallback ? [] : worldRef.current.terms;
          const lightweightWindow = mergeTermWindows(
            baselineTerms,
            lightweightTerms,
            MAX_CLIENT_TERMS,
          );
          let synchronizedTerms = lightweightTerms;

          if (termWindowNeedsRecovery(lightweightWindow, snapshot.currentN)) {
            const recoveryResponse = await fetch(
              `/api/recent?through=${encodeURIComponent(snapshot.generatedThrough)}&limit=${INITIAL_TERM_LIMIT}`,
              { signal: controller.signal, headers: { Accept: "application/json" } },
            );
            if (!recoveryResponse.ok) {
              throw new Error(`term recovery request failed (${recoveryResponse.status})`);
            }
            const recoveredTerms = parseTermsPayload(await recoveryResponse.json());
            const recoveredWindow = mergeTermWindows(
              recoveredTerms,
              lightweightTerms,
              MAX_CLIENT_TERMS,
            );
            if (termAtOrBefore(recoveredWindow.terms, BigInt(snapshot.currentN)) === undefined) {
              throw new Error("term recovery did not include visible world history");
            }
            synchronizedTerms = recoveredWindow.terms;
          }

          if (disposed) return;
          hasInitialTerms = true;
          setServerOffsetMs(
            serverOffsetAtRequestMidpoint(
              snapshot.serverTimeMs,
              snapshotRequestStartedAtMs,
              snapshotResponseReceivedAtMs,
            ),
          );
          setWorld((current) => ({
            snapshot,
            terms: mergeTermWindows(
              current.usingFallback ? [] : current.terms,
              synchronizedTerms,
              MAX_CLIENT_TERMS,
            ).terms,
            connection: "live",
            lastSyncMs: snapshotResponseReceivedAtMs,
            usingFallback: false,
          }));
          reconnectAttempt = 0;
        } catch (error) {
          if (disposed || controller.signal.aborted) return;
          console.warn("world_sync_failed", { error: error instanceof Error ? error.message : String(error) });
          setWorld((current) => ({
            ...current,
            connection: current.usingFallback ? "offline" : "reconnecting",
          }));
        } finally {
          refreshPromise = null;
        }
      })();
      return refreshPromise;
    };
    refreshRef.current = refresh;

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) return;
      const delay = Math.min(30_000, 1_000 * 2 ** reconnectAttempt) + Math.random() * 400;
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed || !navigator.onLine) return;
      socket?.close();
      socket = new WebSocket(websocketUrl());
      socket.addEventListener("open", () => {
        reconnectAttempt = 0;
        socket?.send("ping");
      });
      socket.addEventListener("message", (event) => {
        if (typeof event.data !== "string" || event.data === "pong") return;
        try {
          const message = extractMessageData(JSON.parse(event.data));
          if (!message) return;
          if (message.type === "snapshot" && message.data) {
            const snapshot = parseWorldSnapshot(message.data);
            setServerOffsetMs(snapshot.serverTimeMs - Date.now());
            setWorld((current) => ({ ...current, snapshot, connection: "live", lastSyncMs: Date.now(), usingFallback: false }));
            void refresh();
          } else if (message.type === "batch" && message.data) {
            const batch = parseWorldBatch(message.data);
            setServerOffsetMs(batch.serverTimeMs - Date.now());
            setWorld((current) => ({
              ...current,
              snapshot: {
                ...current.snapshot,
                serverTimeMs: batch.serverTimeMs,
                generatedThrough: batch.generatedThrough,
                catchingUp: batch.catchingUp,
              },
              terms: mergeTermWindows(
                current.usingFallback ? [] : current.terms,
                batch.terms,
                MAX_CLIENT_TERMS,
              ).terms,
              connection: "live",
              lastSyncMs: Date.now(),
              usingFallback: false,
            }));
          } else if (message.type === "world-update") {
            void refresh();
          }
        } catch (error) {
          console.warn("world_socket_message_rejected", { error: error instanceof Error ? error.message : String(error) });
        }
      });
      socket.addEventListener("close", () => {
        if (disposed) return;
        setWorld((current) => ({ ...current, connection: current.usingFallback ? "offline" : "reconnecting" }));
        scheduleReconnect();
      });
      socket.addEventListener("error", () => socket?.close());
    };

    const onOnline = () => {
      void refresh();
      connect();
    };
    const onOffline = () => setWorld((current) => ({ ...current, connection: "offline" }));
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };

    void refresh().finally(connect);
    const poll = window.setInterval(() => void refresh(), 60_000);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disposed = true;
      controller.abort();
      socket?.close();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      window.clearInterval(poll);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
      refreshRef.current = null;
    };
  }, []);

  const liveN = useMemo(() => {
    const estimatedServerNow = clock + serverOffsetMs;
    return logicalNAt(
      world.snapshot.startedAtMs,
      world.snapshot.tickMs,
      estimatedServerNow,
      world.snapshot.generatedThrough,
    );
  }, [clock, serverOffsetMs, world.snapshot]);

  const liveTerm = termAtOrBefore(world.terms, liveN) ?? world.snapshot.current;
  const liveDisplayTerms = useMemo(
    () => selectTermWindowAtOrBefore(world.terms, liveN, DISPLAY_TERM_LIMIT),
    [liveN, world.terms],
  );
  const displayTerm = frozenDisplay?.term ?? liveTerm;
  const displayN = BigInt(displayTerm.n);
  const behindBy = liveN > displayN ? liveN - displayN : 0n;
  const displayTerms = frozenDisplay?.terms ?? liveDisplayTerms;

  const recentTerms = useMemo(() => displayTerms.slice(-20).reverse(), [displayTerms]);

  const pauseDisplay = useCallback(
    () => setFrozenDisplay(freezeWorldDisplay(liveTerm, liveDisplayTerms, DISPLAY_TERM_LIMIT)),
    [liveDisplayTerms, liveTerm],
  );
  const returnToLive = useCallback(() => {
    setFrozenDisplay(null);
    void refreshRef.current?.();
  }, []);
  const refresh = useCallback(async () => {
    await refreshRef.current?.();
  }, []);

  return {
    world,
    liveTerm,
    displayTerm,
    displayTerms,
    liveTerms: liveDisplayTerms,
    recentTerms,
    liveN,
    behindBy,
    isPaused: frozenDisplay !== null,
    pauseDisplay,
    returnToLive,
    refresh,
  };
}
